import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import './css/Toolbar.css';
// import { useSelected } from './SelectedContext';
import { useNavigate } from 'react-router-dom';
import SidebarMenu from './SidebarMenu';
import { useLocation } from 'react-router-dom';
import UserCircleIcon from './icons/UserCircleIcon';
import { useAuth } from './AuthContext';
import MenuIcon from './icons/MenuIcon';
import { useParams } from 'react-router-dom';
import { ProstayGuestDto, Customer } from './interfaces';
import { DeviceService } from './DeviceService';
import DeleteConfirmation from './components/DeleteConfirmationPopup';

export interface Property {
    name: string;
    propertyId: string;
    displayName: string;
    currency?: string;
    pricesFormat?: string;
}

export interface CombinedSearchResult {
    type: 'prostay' | 'tableview';
    firstName: string;
    lastName: string;
    email?: string;
    roomNumber?: string;
    reservationNumber?: string;
    reservationStatus?: string;
    id?: string;
    prostayGuest?: ProstayGuestDto;
    customer?: Customer;
}

interface ToolbarProps {
    onSelectGuest: (customerEmail: string, customerName: string, roomNumber: string, reservationNumber: string, result?: CombinedSearchResult) => void;
    isSearchMenuOpen: boolean;
    /** Order-view header details. When on the table-order route these replace
     *  the notification/help/account buttons in the top-right. */
    orderGuests?: number;
    orderServer?: string;
    orderTime?: string;
}


const Toolbar: React.FC<ToolbarProps> = ({ onSelectGuest, isSearchMenuOpen, orderGuests, orderServer, orderTime }) => {
    const { isAuthenticated, logout } = useAuth();
    const [searchMenuOpen, setSearchMenuOpen] = useState<boolean>(isSearchMenuOpen);
    const profileDropdownRef = useRef<HTMLDivElement>(null);
    const profileDropdownToggleRef = useRef<HTMLButtonElement>(null);
    const [searchResults, setSearchResults] = useState<CombinedSearchResult[]>([]);
    const [searchGuestTerm, setSearchGuestTerm] = useState('');
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setSearchMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setSearchMenuOpen(isSearchMenuOpen);
    }, [isSearchMenuOpen]);

    const toggleSearchMenu = () => {
        console.log("trying to open search")
        setSearchMenuOpen(!searchMenuOpen);
    };

    const [searchingGuests, setSearchingGuests] = useState(false);
    const [, setRequestCounter] = useState(0);

    const handleGuestSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = (event.target as HTMLInputElement).value;
        setSearchGuestTerm(value);

        if (value !== '') {
            setSearchMenuOpen(true);
        } else {
            setSearchMenuOpen(false);
        }

        fetchAutocompleteOptions(value);
    };

    const fetchAutocompleteOptions = async (searchValue: string) => {
        if (!searchValue) {
            setSearchResults([]);
            return;
        }

        setSearchingGuests(true);
        setRequestCounter((prev) => prev + 2);

        const combinedResults: CombinedSearchResult[] = [];

        // Fetch Prostay guests
        try {
            const prostayPayload = {
                currentPage: 1,
                searchTerm: searchValue,
                pageSize: 1000,
                filterDuplicatesByEmail: false
            };

            const prostayResponse = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/search-prostay-guest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(prostayPayload),
                credentials: 'include',
            });

            if (prostayResponse.ok) {
                const prostayData = await prostayResponse.json();
                const prostayResults: CombinedSearchResult[] = (prostayData.data || []).map((guest: ProstayGuestDto) => ({
                    type: 'prostay' as const,
                    firstName: guest.firstName || '',
                    lastName: guest.lastName || '',
                    email: guest.email,
                    roomNumber: guest.roomNumber,
                    reservationNumber: guest.reservationNumber,
                    reservationStatus: guest.reservationStatus,
                    prostayGuest: guest,
                }));
                combinedResults.push(...prostayResults);
            }
        } catch (error) {
            console.error('Error fetching Prostay guests:', error);
        } finally {
            setRequestCounter((prev) => prev - 1);
        }

        // Fetch TableView customers
        try {
            const customerPayload = {
                currentPage: 1,
                searchTerm: searchValue,
                pageSize: 1000,
            };

            const customerResponse = await fetch(`${process.env.REACT_APP_API_URL}/Customer/filter`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(customerPayload),
                credentials: 'include',
            });

            if (customerResponse.ok) {
                const customerData = await customerResponse.json();
                const customerResults: CombinedSearchResult[] = (customerData.data || []).map((customer: Customer) => ({
                    type: 'tableview' as const,
                    firstName: customer.firstName || '',
                    lastName: customer.lastName || '',
                    email: customer.email,
                    id: customer.id,
                    customer,
                }));
                combinedResults.push(...customerResults);
            }
        } catch (error) {
            console.error('Error fetching TableView customers:', error);
        } finally {
            setRequestCounter((prev) => {
                const newCount = prev - 1;
                if (newCount === 0) {
                    setSearchingGuests(false);
                }
                return newCount;
            });
        }

        setSearchResults(combinedResults);
    };

    const handleStartOrderForCustomer = (customerEmail: string, customerName: string, roomNumber: string, reservationNumber: string, result?: CombinedSearchResult) => {
        console.log(customerEmail + " " + customerName + " " + roomNumber);

        if (isTableOrderRoute) {
            setSearchMenuOpen(false);
            onSelectGuest(customerEmail, customerName, roomNumber, reservationNumber, result);
        }
    }

    const navigate = useNavigate();
    const location = useLocation();
    const { tableId } = useParams();
    const { orderId } = useParams();

    const isTableOrderRoute = location.pathname.startsWith('/tables') && tableId;
    const isOrderRoute = location.pathname.startsWith('/orders') && orderId;
    const isWaiterRoute = location.pathname.startsWith('/waiter-selection');

    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState<boolean>(false);
    const mobileDrawerRef = useRef<HTMLDivElement>(null);

    // const { setSelectedOption } = useSelected();

    // const handleSelectOptionFromMenu = (option: string) => {
    //     setSelectedOption(option);
    //     closeDrawer();
    // };


    // const closeDrawer = () => {
    //     setIsDrawerOpen(false);
    // };

    const toggleSidebar = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsMobileDrawerOpen(!isMobileDrawerOpen);
    };

    const closeSidebar = () => {
        setIsMobileDrawerOpen(false);
    };

    const [currentProperty, setCurrentProperty] = useState<Property | null>(null);

    const fetchProperty = async () => {
        const storedProperty = localStorage.getItem('currentProperty');
        if (storedProperty) {
            setCurrentProperty(JSON.parse(storedProperty));
        } else {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Company`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Network response was not ok');
                const data = await response.json();
                localStorage.setItem('currentProperty', JSON.stringify(data));
                setCurrentProperty(data);
            } catch (error) {
                console.error('Error fetching data: ', error);
            }
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        const fetchData = async () => {
            await fetchProperty();
        };
        fetchData();
    }, [isAuthenticated]);

    const getStatusColor = (status: string) => {
        if (status == 'Canceled') {
            return 'rgb(255, 75, 85)';
        }
        else if (status == 'Confirmed') {
            return 'rgb(39, 174, 96)';
        }
        else if (status == 'In House') {
            return 'rgb(215, 119, 119)';
        }
        else if (status == 'Pending') {
            return 'rgb(150, 215, 210)';
        }
        else {
            return 'rgb(56, 56, 56)'
        }
    }

    useEffect(() => {
        setSearchGuestTerm('');
        setSearchResults([]);
        setSearchMenuOpen(false);
    }, [location]);

    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState<boolean>(false);

    const handleButtonClick = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleOptionClick = (path: string) => {
        navigate(path);
        setDropdownOpen(false);
    };

    const handleLogout = async () => {
        try {
            await DeviceService.unregisterDevice();
            console.log('Device unregistered');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Authentication/logout`, {
                method: 'POST',
                credentials: 'include',
            });

            if (response.ok) {
                localStorage.clear();
                logout();
                localStorage.removeItem('deviceId');
                localStorage.removeItem('propertyId');
                navigate('/login');
            } else {
                console.error('Logout failed:', response.statusText);
            }
        } catch (error) {
            console.error('Failed to logout:', error);
        }
    };

    const handleBackTablesClick = () => {
        if (!isOrderRoute) {
            navigate('/tables')
        }
    }

    return (
        <>
        <header className="toolbar" style={{ display: !isWaiterRoute ? 'flex' : 'none' }}>
            <div className="d-flex col-auto align-items-center">
                {
                    (isTableOrderRoute || isOrderRoute) ?
                        <button id='tables-back-btn' className='text-btn px-3 gap-1 d-flex align-items-center' style={{ fontWeight: 600, fontSize: 16, color: 'var(--color-primary)' }} onClick={handleBackTablesClick}>
                            <svg width="26" height="16" viewBox="0 0 26 19" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: -4 }}>
                                <path d="M1.75 6.25H18.625C21.7316 6.25 24.25 8.7684 24.25 11.875C24.25 14.9816 21.7316 17.5 18.625 17.5H13M1.75 6.25L6.75 1.25M1.75 6.25L6.75 11.25" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            Tables
                        </button>
                        :
                        <h1>{currentProperty?.displayName}</h1>
                }
            </div>

            <div className={`mobile-drawer ${isMobileDrawerOpen ? 'open' : ''}`} ref={mobileDrawerRef} style={{ zIndex: '9999' }}>
                <SidebarMenu isOpen={isMobileDrawerOpen} closeSidebar={closeSidebar} />
            </div>



            {
                <div className="search-container col-5 d-flex justify-content-center" onClick={() => { if (!searchMenuOpen) toggleSearchMenu() }} ref={searchRef} style={{ position: 'fixed', zIndex: 9, top: 9, left: '50%', transform: 'translateX(-50%)', }}>
                    <svg
                        className="search-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 30 30"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M26.25 26.25L20.8213 20.8213M20.8213 20.8213C21.7499 19.8927 22.4865 18.7902 22.989 17.5769C23.4916 16.3637 23.7503 15.0633 23.7503 13.75C23.7503 12.4368 23.4916 11.1364 22.989 9.92308C22.4865 8.70979 21.7499 7.60737 20.8213 6.67876C19.8927 5.75015 18.7902 5.01354 17.5769 4.51098C16.3637 4.00842 15.0633 3.74976 13.75 3.74976C12.4368 3.74976 11.1364 4.00842 9.92308 4.51098C8.70979 5.01354 7.60737 5.75015 6.67876 6.67876C4.80335 8.55418 3.74976 11.0978 3.74976 13.75C3.74976 16.4022 4.80335 18.9459 6.67876 20.8213C8.55418 22.6967 11.0978 23.7503 13.75 23.7503C16.4022 23.7503 18.9459 22.6967 20.8213 20.8213Z"
                            stroke="#667085"
                            strokeWidth="1.875"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search by guest name or email"
                        value={searchGuestTerm}
                        onClick={() => { if (!searchMenuOpen) setSearchMenuOpen(true) }}
                        onChange={handleGuestSearchChange}
                        className="search-input"
                    />
                    {
                        searchMenuOpen &&
                        <>
                            {
                                searchingGuests ?
                                    <>
                                        <div className='search-menu p-3' style={{ textAlign: 'center', position: 'fixed', zIndex: 9, left: 14, top: 35, backgroundColor: '#fff', width: 'calc(100% - 28px)' }}>
                                            <div className="search-spinner"></div>
                                        </div>
                                    </>
                                    :
                                    <>
                                        {
                                            searchResults.length > 0 ? (
                                                <div className='search-menu' style={{ position: 'fixed', paddingInline: 0, zIndex: 9, top: 35, left: 14, backgroundColor: '#fff', width: 'calc(100% - 28px)' }}>
                                                    <small style={{ paddingInline: 10, fontSize: 12, fontFamily: 'Inter' }}>All results for "{searchGuestTerm}":</small>
                                                    <div className='results-container'>
                                                        {
                                                            searchResults.map((result, index) => {
                                                                return (
                                                                    <div key={index} className='search-result' onClick={() => handleStartOrderForCustomer(result.email || "", result.firstName + " " + result.lastName, result.roomNumber || "", result.reservationNumber || "", result)}>
                                                                        <div className='d-flex justify-content-between align-items-start'>
                                                                            <div className='d-flex gap-1 align-items-center'>
                                                                                <span style={{
                                                                                    backgroundColor: result.type === 'prostay' ? '#E0E7FF' : '#D1FAE5',
                                                                                    color: result.type === 'prostay' ? '#3730A3' : '#065F46',
                                                                                    padding: '2px 6px',
                                                                                    borderRadius: '4px',
                                                                                    fontSize: 10,
                                                                                    fontWeight: 600,
                                                                                    marginRight: 6
                                                                                }}>
                                                                                    {result.type === 'prostay' ? 'Prostay' : 'TableView'}
                                                                                </span>
                                                                                <span style={{ color: '#1643e0', fontWeight: 600, fontSize: 14 }}>{result.firstName} {result.lastName}</span>
                                                                                {result.type === 'prostay' && (
                                                                                    <span style={{ fontSize: 12 }}>{result.reservationNumber} {result.roomNumber ? "-  " + result.roomNumber : ""} </span>
                                                                                )}
                                                                                {result.type === 'tableview' && result.email && (
                                                                                    <span style={{ fontSize: 12, color: '#6B7280' }}>{result.email}</span>
                                                                                )}
                                                                            </div>
                                                                            <div className='col d-flex flex-column align-items-end'>
                                                                                {result.type === 'prostay' && (
                                                                                    <small style={{ color: getStatusColor(result.reservationStatus || ""), fontWeight: 500 }}>{result.reservationStatus}</small>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                            )
                                                :
                                                (
                                                    <>
                                                        <div className='search-menu p-3' style={{ textAlign: 'center', position: 'fixed', zIndex: 9, left: 14, top: 35, backgroundColor: '#fff', width: 'calc(100% - 28px)' }}>
                                                            <div style={{ color: '#344054', fontFamily: '"Inter", sans-serif', fontSize: '14px' }}>Type to see search results</div>
                                                            <p style={{ color: '#98A2B3' }}>Search guest names or email addresses</p>
                                                        </div>
                                                    </>
                                                )
                                        }
                                    </>
                            }
                        </>
                    }
                </div>
            }


            <div className="d-flex col-2 col-lg-3 justify-content-end align-items-center tb-btns col-auto">
                {isTableOrderRoute ? (
                    <div className="tb-orderinfo">
                        <div className="tb-oi">
                            <span className="tb-oi__k">Guests</span>
                            <span className="tb-oi__v">{orderGuests && orderGuests > 0 ? orderGuests : '—'}</span>
                        </div>
                        <span className="tb-oi__div" aria-hidden="true" />
                        <div className="tb-oi">
                            <span className="tb-oi__k">Server</span>
                            <span className="tb-oi__v" title={orderServer || undefined}>{orderServer || '—'}</span>
                        </div>
                        <span className="tb-oi__div" aria-hidden="true" />
                        <div className="tb-oi">
                            <span className="tb-oi__k">Time</span>
                            <span className="tb-oi__v">{orderTime || '—'}</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <button className="notifications-container">
                            <svg width="20" height="20" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.5903 35C16.7655 36.0373 18.3093 36.6667 20 36.6667C21.6907 36.6667 23.2345 36.0373 24.4097 35M30 13.3334C30 10.6812 28.9464 8.13767 27.0711 6.26231C25.1957 4.38694 22.6522 3.33337 20 3.33337C17.3478 3.33337 14.8043 4.38694 12.9289 6.26231C11.0536 8.13767 10 10.6812 10 13.3334C10 18.4837 8.70078 22.01 7.24944 24.3424C6.02521 26.3098 5.4131 27.2936 5.43554 27.568C5.46039 27.8718 5.52476 27.9877 5.76962 28.1693C5.99076 28.3334 6.98765 28.3334 8.98142 28.3334H31.0186C33.0123 28.3334 34.0092 28.3334 34.2304 28.1693C34.4752 27.9877 34.5396 27.8718 34.5645 27.568C34.5869 27.2936 33.9748 26.3098 32.7506 24.3424C31.2992 22.01 30 18.4837 30 13.3334Z" stroke="#475467" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                        <button className='col-6 mobile-menu-button' onClick={toggleSidebar}>
                            <MenuIcon color='white' />
                        </button>
                        <button className="help-container">
                            <svg width="20" height="20" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.15 15C15.5418 13.8862 16.3153 12.9469 17.3333 12.3486C18.3513 11.7503 19.5482 11.5316 20.712 11.7312C21.8758 11.9309 22.9314 12.5359 23.6918 13.4393C24.4522 14.3426 24.8684 15.4859 24.8667 16.6667C24.8667 20 19.8667 21.6667 19.8667 21.6667M20 28.3334H20.0167M36.6667 20C36.6667 29.2048 29.2048 36.6667 20 36.6667C10.7953 36.6667 3.33334 29.2048 3.33334 20C3.33334 10.7953 10.7953 3.33337 20 3.33337C29.2048 3.33337 36.6667 10.7953 36.6667 20Z" stroke="#475467" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                        <button className='notifications-container' onClick={handleButtonClick} ref={profileDropdownToggleRef}>
                            <UserCircleIcon width={20} height={20} stroke={1}
                                color='#475467' />
                        </button>
                        {dropdownOpen && (
                            <div className="dropdown-menu mt-5" ref={profileDropdownRef} style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ paddingBlock: '1px', paddingLeft: '15px', paddingRight: '15px', fontSize: '12px' }}>
                                    Property ID: komodo-resort {currentProperty?.propertyId}

                                </div>
                                <hr style={{ marginBlock: '7px' }} />

                                <div className="dropdown-option" onClick={() => handleOptionClick('/profile')}>
                                    <UserCircleIcon width={20} height={20}
                                        color='black' />
                                    My Profile
                                </div>
                                <div onClick={() => { setDropdownOpen(false); setShowLogoutConfirm(true); }} className="dropdown-option" >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                        <polyline points="16 17 21 12 16 7" />
                                        <line x1="21" y1="12" x2="9" y2="12" />
                                    </svg>
                                    Log Out
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </header>
            {showLogoutConfirm && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }}>
                    <div className="dark-popup-overlay" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} onClick={() => setShowLogoutConfirm(false)}></div>
                    <DeleteConfirmation
                        title="Logout"
                        content="Are you sure you want to log out?"
                        onClose={() => setShowLogoutConfirm(false)}
                        onDelete={handleLogout}
                    />
                </div>
            )}
        </>
    );
};

export default Toolbar;
